import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowRightLeft, Ban, BadgeCheck, CheckCircle2, CircleHelp, Clock, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { ReceiptPreviewDialog } from './receipt-preview-dialog';
import type { BankTransfer, BankTransferStatus } from '@/lib/bank-transfers-client';

const VERIFICATION_STATUS_CONFIG: Record<
    BankTransferStatus,
    { labelKey: string; icon: typeof CheckCircle2; bg: string; text: string; iconColor: string }
> = {
    verified: { labelKey: 'verified', icon: CheckCircle2, bg: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800/60', text: 'text-emerald-800 dark:text-emerald-300', iconColor: 'text-emerald-500 dark:text-emerald-400' },
    mismatch: { labelKey: 'mismatch', icon: AlertTriangle, bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800/60', text: 'text-amber-800 dark:text-amber-300', iconColor: 'text-amber-500 dark:text-amber-400' },
    pending: { labelKey: 'pending', icon: Clock, bg: 'bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800/60', text: 'text-blue-800 dark:text-blue-300', iconColor: 'text-blue-400' },
};

function MatchIndicator({ value }: { value?: boolean }) {
    if (value === true) return <CheckCircle2 className="h-3 w-3 text-emerald-500 dark:text-emerald-400" />;
    if (value === false) return <Ban className="h-3 w-3 text-red-400" />;
    return <CircleHelp className="h-3 w-3 text-muted-foreground/50" />;
}

interface TransferVerificationDisplayProps {
    transfer: BankTransfer;
    /** Display currency fallback when the receipt didn't carry one. */
    currency?: string;
    /** Fresh presigned URL for the archived receipt — caller picks the endpoint (order- or transfer-scoped). */
    getReceiptUrl: () => Promise<{ url: string }>;
    /** Skip the left indent used inside the order detail panel. */
    flush?: boolean;
}

/**
 * The verification card for one transfer proof: status pill, extracted fields with match
 * indicators, confidence line and the "view receipt" action. Shared by the order detail panel
 * and the Bank Transfers page.
 */
export function TransferVerificationDisplay({ transfer, currency, getReceiptUrl, flush }: TransferVerificationDisplayProps) {
    const { t } = useTranslation('orders');
    const [viewerOpen, setViewerOpen] = useState(false);
    const config = VERIFICATION_STATUS_CONFIG[transfer.status];
    const Icon = config.icon;
    const verification = transfer.verification;
    const ext = verification?.extractedData;
    const match = verification?.matchDetails;
    const manual = verification?.manualVerification;
    const displayCurrency = transfer.currency ?? currency ?? '';

    // The archived S3 copy needs a fresh presigned URL per view; the expiring Gupshup URL is used
    // as-is when nothing was archived. Either way the viewer dialog owns the loading/error states.
    const resolveReceiptUrl = transfer.receiptKey
        ? () => getReceiptUrl().then((r) => r.url)
        : () => Promise.resolve(transfer.imageUrl ?? '');

    return (
        <div className={`${flush ? '' : 'ml-5 '}rounded-md border p-2.5 space-y-2 ${config.bg}`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    <ArrowRightLeft className="h-3.5 w-3.5 text-muted-foreground/70" />
                    <span className={`text-xs font-medium ${config.text}`}>{t('transfer.title')}</span>
                    {(transfer.receiptKey || transfer.imageUrl) && (
                        <button
                            type="button"
                            onClick={() => setViewerOpen(true)}
                            className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                        >
                            <Eye className="h-3.5 w-3.5" />
                            {t('transfer.viewReceipt')}
                        </button>
                    )}
                </div>
                <div className={`flex items-center gap-1 text-xs font-medium ${config.text}`}>
                    <Icon className={`h-3.5 w-3.5 ${config.iconColor}`} />
                    {t(`transfer.status.${config.labelKey}`)}
                </div>
            </div>

            {/* A `verified` pill says nothing about HOW it got verified, and a manual confirmation is
                the one case where no comprobante was ever checked — so it is stated outright rather
                than left to be inferred from an empty extraction block. */}
            {manual && (
                <div className="rounded border border-emerald-300/70 dark:border-emerald-700/60 bg-emerald-100/60 dark:bg-emerald-900/30 px-2 py-1.5 space-y-0.5">
                    <p className="flex items-center gap-1.5 text-xs font-medium text-emerald-800 dark:text-emerald-300">
                        <BadgeCheck className="h-3.5 w-3.5 shrink-0" />
                        {t('transfer.manual.title')}
                    </p>
                    <p className="text-[11px] text-emerald-700 dark:text-emerald-300/80">
                        {t(manual.hadReceipt ? 'transfer.manual.overReceipt' : 'transfer.manual.noReceipt', {
                            status: t(`transfer.status.${manual.previousStatus ?? 'pending'}`),
                        })}
                        {` — ${format(new Date(manual.at), 'dd/MM HH:mm')}`}
                    </p>
                    {manual.reason && (
                        <p className="text-[11px] italic text-emerald-700 dark:text-emerald-300/80 break-words">
                            “{manual.reason}”
                        </p>
                    )}
                </div>
            )}

            <ReceiptPreviewDialog
                open={viewerOpen}
                onOpenChange={setViewerOpen}
                resolveUrl={resolveReceiptUrl}
                sourceName={transfer.receiptKey}
            />

            {ext && (
                <div className="space-y-1 text-xs">
                    {ext.amount != null && (
                        <div className="flex items-center gap-1.5">
                            <MatchIndicator value={match?.amountMatch} />
                            <span className="text-muted-foreground">{t('transfer.amount')}: {displayCurrency} {ext.amount}</span>
                        </div>
                    )}
                    {ext.recipientAccount && (
                        <div className="flex items-center gap-1.5">
                            <MatchIndicator value={match?.accountMatch} />
                            <span className="text-muted-foreground">{t('transfer.account')}: {ext.recipientAccount}</span>
                        </div>
                    )}
                    {ext.recipientName && (
                        <div className="flex items-center gap-1.5">
                            <MatchIndicator value={match?.nameMatch} />
                            <span className="text-muted-foreground">{t('transfer.holder')}: {ext.recipientName}</span>
                        </div>
                    )}
                    {ext.senderName && (
                        <div className="flex items-center gap-1.5">
                            <span className="h-3 w-3" />
                            <span className="text-muted-foreground">{t('transfer.sender')}: {ext.senderName}</span>
                        </div>
                    )}
                    {ext.reference && (
                        <div className="flex items-center gap-1.5">
                            <span className="h-3 w-3" />
                            <span className="text-muted-foreground">{t('transfer.reference')}: {ext.reference}</span>
                        </div>
                    )}
                    {ext.date && (
                        <div className="flex items-center gap-1.5">
                            <span className="h-3 w-3" />
                            <span className="text-muted-foreground">{t('transfer.date')}: {ext.date}</span>
                        </div>
                    )}
                </div>
            )}

            {verification?.confidence && (
                <div className="text-[10px] text-muted-foreground/70">
                    {t('transfer.confidenceLabel')}: {t(`transfer.confidence.${verification.confidence}`)}
                    {verification.verifiedAt && ` — ${format(new Date(verification.verifiedAt), 'dd/MM HH:mm')}`}
                </div>
            )}
        </div>
    );
}
